import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import useTooltip from '../../hooks/useTooltip';
import ArrowDown from '../../../assets/simple-arrow-down.svg';
import Button from './Button';
import styles from '../../styles/views/components/GroupedButtons.module.scss';

const millisecondsInHour = 1000 * 60 * 10; // milliseconds * seconds * minutes

const GroupedButtons = ({
  buttons,
  mainButtonText,
  leftIcon,
  shouldDisableButtons,
  extraStyles,
}) => {
  useTooltip();
  const wrapperRef = useRef(null);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [buttonsUpdatedState, setButtonsUpdatedState] = useState({});

  const toggleSubMenuState = () => setIsSubMenuOpen((prev) => !prev);

  const handleClickOutside = (event) => {
    if (wrapperRef && !wrapperRef.current?.contains(event.target)) {
      setIsSubMenuOpen(false);
    }
  };

  useEffect(() => {
    const currentTime = new Date().getTime();

    const buttonsState = buttons.reduce((acc, btn) => {
      const buttonUpdatedTime = Number(localStorage.getItem(btn.name));
      const isUpdatedRecently = (currentTime - buttonUpdatedTime) < millisecondsInHour;
      return { ...acc, [btn.name]: isUpdatedRecently };
    }, {});

    setButtonsUpdatedState(buttonsState);
  }, [isSubMenuOpen, buttons]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={[styles.wrapper, isSubMenuOpen && styles.active].join(' ')}
      style={extraStyles}
    >
      <Button
        value={mainButtonText}
        variant="secondary"
        color="red"
        onClick={toggleSubMenuState}
        iconAsComponent={leftIcon}
        iconAsComponentEnd={<ArrowDown />}
        extraStyles={!leftIcon ? { paddingRight: '15px' } : {}}
      />
      {isSubMenuOpen && (
        <div className={styles.buttonsWrapper}>
          {buttons.map((btn) => (btn.isHidden ? null : (btn.isDisabled
            ? (
              <div
                key={btn.name}
                className={[styles.listButton, styles.disable].join(' ')}
              >
                {btn.name}
              </div>
            )
            : (
              <div
                key={btn.name}
                className={[styles.listButton, shouldDisableButtons && buttonsUpdatedState[btn.name] && styles.disable].join(' ')}
                onClick={() => {
                  btn.onClick();
                  localStorage.setItem(btn.name, new Date().getTime());
                  toggleSubMenuState();
                }}
                data-tip={(shouldDisableButtons && buttonsUpdatedState[btn.name])
                  ? t('tooltips.alreadyUpdated', { ns: i18next.Namespace.COMMON })
                  : btn.tooltip ? btn.tooltip : ''}
              >
                {btn.name}
              </div>
            ))))}
        </div>
      )}
    </div>
  );
};

GroupedButtons.propTypes = {
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ).isRequired,
  mainButtonText: PropTypes.string.isRequired,
  leftIcon: PropTypes.node.isRequired,
  shouldDisableButtons: PropTypes.bool.isRequired,
  extraStyles: PropTypes.shape({}).isRequired,
};

export default GroupedButtons;
